import { PageLoading, PageError } from '../../components/common/PageState';
import React, { useMemo, useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import {
  ChartColumn,
  ChevronsDownUp,
  ChevronsUpDown,
  GripVertical,
  List as ListIcon,
  Network,
  Printer,
  Users,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { hierarchy, tree } from 'd3-hierarchy';
import { useGetEmployeesQuery, useUpdateEmployeeMutation, Employee } from '../../store/slices/employeeSlice';
import { useGetDepartmentsQuery, Department } from '../../store/slices/departmentSlice';
import { useAuth } from '../../contexts/AuthContext';
import {
  buildEmployeeHierarchyLookups,
  getEffectiveManagerEmployeeId,
  isEmployeeDescendant,
  resolveManagerEmployeeId,
} from '../../utils/employeeHierarchy';
import { CountBadge, EmptyState, ErrorState, PageHeader, SearchField, Sheet, StatusPill, Toolbar } from '../../components/ui';
import { reloadPage, useStalled } from '../../components/attendance/attendanceUi';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatNumber, formatTime } from '../../utils/format';
import { readsOnlyOwnRecords } from '../../utils/roles';
import {
  ALL_DEPARTMENTS,
  buildDepartmentSwatches,
  buildElbowPath,
  clampZoom,
  DensityMode,
  departmentKeyOf,
  DepartmentSwatch,
  describeSaveError,
  easeEnter,
  employeeMatchesSearch,
  EmployeeNode,
  getEmployeeTeamLabel,
  INITIAL_ZOOM,
  isSalesEmployee,
  MANAGER_CARD_HEIGHT,
  MANAGER_CARD_WIDTH,
  MAP_PADDING,
  NEUTRAL_SWATCH,
  NO_DEPARTMENT_LABEL,
  OrgLayout,
  OrgViewMode,
  OVERVIEW_ZOOM,
  PERSON_CARD_HEIGHT,
  PERSON_CARD_WIDTH,
  PositionedOrgNode,
  prefersReducedMotion,
  RAIL_MIN_PAGE_WIDTH,
  SalesManagerLane,
  SaveErrorCopy,
  SelectVia,
  sortByName,
  titleLineOf,
  UNASSIGNED_TEAM,
  VisualOrgNode,
  ZOOM_STEP,
} from './org-chart-parts/orgChartModel';
import { buildOrgChartPrintHtml } from './org-chart-parts/orgChartPrint';
import { DeptDot } from './org-chart-parts/OrgChartBits';
import { CanvasHint, DepartmentLegend, ZoomControls } from './org-chart-parts/CanvasChrome';
import OrgNodeCard from './org-chart-parts/OrgNodeCard';
import TeamLane from './org-chart-parts/TeamLane';
import OrgListTable from './org-chart-parts/OrgListTable';
import PlacementDialog from './org-chart-parts/PlacementDialog';
import OrgSummarySheet from './org-chart-parts/OrgSummarySheet';
import {
  PersonDetailActions,
  PersonDetailBody,
  PersonDetailData,
  PersonDetailPanel,
} from './org-chart-parts/PersonDetail';
import { useElementWidth } from './org-chart-parts/useMeasure';

/* New cards rise 4px and fade in (book §10: arrivals move up, ≤300ms). */
const nodeEnter = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: none; }
`;

const ZOOM_ANIMATION_MS = 200;
const SCROLL_ANIMATION_MS = 300;
const VIEW_HEIGHT = { xs: 520, md: 'max(440px, calc(100vh - 400px))' } as const;
const VIEW_TAB_ID = (mode: OrgViewMode) => `org-view-tab-${mode}`;
const VIEW_PANEL_ID = 'org-view-panel';
/** A first load that hasn't answered by now is treated as lost (RTK Query can't retry a pending request). */
const STALLED_LOAD_MS = 15_000;
/** Room kept between a revealed card and the canvas edge. */
const REVEAL_MARGIN = 24;

const cssEscape = (value: string) =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');

// ── Main OrgChart Component ──
const OrgChart: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const {
    data: allEmployees = [],
    isLoading,
    error: employeesError,
    refetch: reloadEmployees,
    fulfilledTimeStamp,
  } = useGetEmployeesQuery();
  const { data: departments = [], isLoading: departmentsLoading, error: departmentsError, refetch: reloadDepartments } = useGetDepartmentsQuery();
  const [updateEmployee, { isLoading: isSaving }] = useUpdateEmployeeMutation();
  const canEditOrg = profile?.role === 'admin' || profile?.role === 'hr';
  const canViewProfiles = profile?.role !== 'employee';
  // The employee role gets the directory, without WFH setup, so it can't count hybrid people.
  const directoryOnly = readsOnlyOwnRecords(profile?.role);
  const loadStalled = useStalled(isLoading || departmentsLoading, 'org-chart', STALLED_LOAD_MS);
  // The detail rail sits beside the chart only when the page itself is wide enough
  // (measured, so the sidebar counts); otherwise the person opens in a sheet.
  const [pageRef, pageWidth] = useElementWidth<HTMLDivElement>();
  const isWide = pageWidth >= RAIL_MIN_PAGE_WIDTH;

  // Only show active employees in the org chart
  const employees = useMemo(
    () => allEmployees.filter((e) => !e.employmentStatus || e.employmentStatus === 'active'),
    [allEmployees]
  );

  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>(ALL_DEPARTMENTS);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Employee | null>(null);
  const [managerId, setManagerId] = useState<string>('');
  const [managerName, setManagerName] = useState<string>('');
  const [teamName, setTeamName] = useState<string>('');
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [teamDragOverName, setTeamDragOverName] = useState<string | null>(null);
  const [teamMoveSavingId, setTeamMoveSavingId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(INITIAL_ZOOM);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [revealId, setRevealId] = useState<string | null>(null);
  const [matchCursor, setMatchCursor] = useState(-1);
  const [viewMode, setViewMode] = useState<OrgViewMode>('tree');
  const [densityMode, setDensityMode] = useState<DensityMode>('managers');
  const [hasScrolledToAnchor, setHasScrolledToAnchor] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
    action?: SaveErrorCopy['action'];
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saveError, setSaveError] = useState<SaveErrorCopy | null>(null);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [railFocusTick, setRailFocusTick] = useState(0);
  const [statsOpen, setStatsOpen] = useState(false);

  // The pan/zoom canvas. A callback ref so listeners re-bind when the tree view remounts.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  const setCanvasRef = useCallback((element: HTMLDivElement | null) => {
    containerRef.current = element;
    setCanvasEl(element);
  }, []);
  const zoomRef = useRef(INITIAL_ZOOM);
  const pendingScrollRef = useRef<{ left: number; top: number } | null>(null);
  const zoomAnimationRef = useRef<number | null>(null);
  const scrollAnimationRef = useRef<number | null>(null);
  const panRef = useRef<{ pointerId: number; x: number; y: number; left: number; top: number; moved: boolean } | null>(null);
  const lastCenteredFilterRef = useRef('');
  // Focus bookkeeping for the detail rail: where a selection came from, and who was just closed.
  const selectionOriginRef = useRef<HTMLElement | null>(null);
  const pendingReturnRef = useRef<string | null>(null);
  const railHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const railFocusHandledRef = useRef(0);

  // ── Data helpers ──
  const employeeHierarchy = useMemo(
    () => buildEmployeeHierarchyLookups(employees),
    [employees]
  );
  const employeeById = employeeHierarchy.employeeById;

  const reportsByManager = useMemo(() => {
    const map = new Map<string, Employee[]>();
    employees.forEach((emp) => {
      const mid = getEffectiveManagerEmployeeId(emp, employeeHierarchy);
      if (!mid) return;
      if (!map.has(mid)) map.set(mid, []);
      map.get(mid)!.push(emp);
    });
    map.forEach((list) => list.sort(sortByName));
    return map;
  }, [employees, employeeHierarchy]);

  const descendantCountById = useMemo(() => {
    const counts = new Map<string, number>();

    const countFor = (employeeId: string, seen = new Set<string>()): number => {
      if (counts.has(employeeId)) return counts.get(employeeId)!;
      if (seen.has(employeeId)) return 0;

      const nextSeen = new Set(seen);
      nextSeen.add(employeeId);
      const reports = reportsByManager.get(employeeId) || [];
      const total = reports.reduce((sum, report) => sum + 1 + countFor(report._id, nextSeen), 0);
      counts.set(employeeId, total);
      return total;
    };

    employees.forEach((employee) => countFor(employee._id));
    return counts;
  }, [employees, reportsByManager]);

  const roots = useMemo(() => {
    return employees
      .filter((emp) => {
        const mid = getEffectiveManagerEmployeeId(emp, employeeHierarchy);
        return !mid || !employeeById.has(mid);
      })
      .sort(sortByName);
  }, [employees, employeeById, employeeHierarchy]);

  const buildNode = useCallback(
    (emp: Employee): EmployeeNode => {
      const reports = reportsByManager.get(emp._id) || [];
      return { ...emp, reports: reports.map(buildNode) };
    },
    [reportsByManager]
  );

  // Find the CEO — Jeffrey Sachs or whichever root has the most descendants
  const ceo = useMemo(() => {
    // Try exact name match first
    const jeff = employees.find(
      (e) => e.name.toLowerCase().includes('jeffrey sachs') || e.name.toLowerCase().includes('jeff sachs')
    );
    if (jeff) return jeff;

    // Fallback: root with the most total descendants
    let bestRoot: Employee | null = null;
    let bestCount = -1;
    roots.forEach((root) => {
      const count = descendantCountById.get(root._id) || 0;
      if (count > bestCount) {
        bestCount = count;
        bestRoot = root;
      }
    });
    return bestRoot;
  }, [employees, roots, descendantCountById]);

  // Sort roots: CEO first, then others alphabetically
  const sortedRoots = useMemo(() => {
    if (!ceo) return roots;
    const others = roots.filter((r) => r._id !== ceo._id);
    return [ceo, ...others];
  }, [roots, ceo]);

  // Auto-expand top levels on mount
  useEffect(() => {
    if (employees.length > 0 && expandedNodes.size === 0) {
      const initial = new Set<string>();
      // Expand CEO and their direct reports by default
      if (ceo) {
        initial.add(ceo._id);
        const ceoReports = reportsByManager.get(ceo._id) || [];
        ceoReports.forEach((r) => initial.add(r._id));
      }
      // Expand other roots too (just the root level)
      roots.forEach((root) => {
        initial.add(root._id);
      });
      setExpandedNodes(initial);
    }
  }, [employees, roots, reportsByManager, ceo]);

  // ── Departments: fixed series colors for the session (book §13.3) ──
  const departmentSwatches = useMemo(() => buildDepartmentSwatches(employees, departments), [employees, departments]);
  const swatchByKey = useMemo(
    () => new Map<string, DepartmentSwatch>(departmentSwatches.map((swatch) => [swatch.key, swatch])),
    [departmentSwatches]
  );
  const departmentOf = useCallback(
    (employee: Employee) => {
      const swatch = swatchByKey.get(departmentKeyOf(employee));
      return {
        label: swatch?.label ?? (employee.department?.name || NO_DEPARTMENT_LABEL),
        color: swatch?.color ?? NEUTRAL_SWATCH,
      };
    },
    [swatchByKey]
  );
  const departmentOptions = useMemo(
    () => [...departmentSwatches].sort((a, b) => Number(a.isNone) - Number(b.isNone) || a.label.localeCompare(b.label)),
    [departmentSwatches]
  );
  const realDepartmentCount = departmentSwatches.filter((swatch) => !swatch.isNone).length;
  const activeDepartment = departmentFilter === ALL_DEPARTMENTS ? null : swatchByKey.get(departmentFilter) ?? null;

  // ── Search + department filter ──
  const searchActive = Boolean(searchTerm.trim());
  const departmentActive = departmentFilter !== ALL_DEPARTMENTS;
  const filterActive = searchActive || departmentActive;

  const matchesFilters = useCallback(
    (employee: Employee) =>
      employeeMatchesSearch(employee, searchTerm)
      && (departmentFilter === ALL_DEPARTMENTS || departmentKeyOf(employee) === departmentFilter),
    [searchTerm, departmentFilter]
  );

  const searchMatches = useMemo(() => {
    const matches = new Set<string>();
    if (!filterActive) return matches;
    employees.forEach((emp) => {
      if (matchesFilters(emp)) {
        matches.add(emp._id);
      }
    });
    return matches;
  }, [employees, filterActive, matchesFilters]);

  // Path matches: all ancestors of search matches
  const pathMatches = useMemo(() => {
    const paths = new Set<string>();
    if (searchMatches.size === 0) return paths;

    const getAncestors = (empId: string) => {
      const emp = employeeById.get(empId);
      if (!emp) return;
      const mid = getEffectiveManagerEmployeeId(emp, employeeHierarchy);
      if (mid && employeeById.has(mid)) {
        paths.add(mid);
        getAncestors(mid);
      }
    };

    searchMatches.forEach((id) => getAncestors(id));
    return paths;
  }, [searchMatches, employeeById, employeeHierarchy]);

  // Auto-expand path to search results
  useEffect(() => {
    if (searchMatches.size > 0) {
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        pathMatches.forEach((id) => next.add(id));
        searchMatches.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [searchMatches, pathMatches]);

  // Visible nodes (for filtering in search mode)
  const visibleNodes = useMemo(() => {
    const visible = new Set<string>();
    if (filterActive) {
      searchMatches.forEach((id) => visible.add(id));
      pathMatches.forEach((id) => visible.add(id));
      // Also show roots that lead to matches
      roots.forEach((r) => {
        if (searchMatches.has(r._id) || pathMatches.has(r._id)) {
          visible.add(r._id);
        }
      });
    } else {
      employees.forEach((emp) => visible.add(emp._id));
    }
    return visible;
  }, [filterActive, searchMatches, pathMatches, employees, roots]);

  const effectiveDensityMode: DensityMode = filterActive ? 'all' : densityMode;

  const orgLayout = useMemo<OrgLayout>(() => {
    const cardWidth = effectiveDensityMode === 'managers' ? MANAGER_CARD_WIDTH : PERSON_CARD_WIDTH;
    const cardHeight = effectiveDensityMode === 'managers' ? MANAGER_CARD_HEIGHT : PERSON_CARD_HEIGHT;
    const horizontalStep = cardWidth + (effectiveDensityMode === 'managers' ? 42 : 28);
    const depthStep = cardHeight + (effectiveDensityMode === 'managers' ? 62 : 46);

    const buildVisualNode = (node: EmployeeNode): VisualOrgNode => {
      const visibleReports = node.reports.filter((report) => visibleNodes.has(report._id));
      const groupedReports = effectiveDensityMode === 'managers'
        ? visibleReports.filter((report) => report.reports.length === 0)
        : [];
      const childReports = effectiveDensityMode === 'managers'
        ? visibleReports.filter((report) => report.reports.length > 0)
        : visibleReports;

      return {
        id: node._id,
        employee: node,
        children: expandedNodes.has(node._id) ? childReports.map(buildVisualNode) : [],
        groupedReports,
        childCount: childReports.length,
        directCount: node.reports.length,
        totalDescendants: descendantCountById.get(node._id) || 0,
      };
    };

    const rootChildren = sortedRoots
      .filter((root) => visibleNodes.has(root._id))
      .map((root) => buildVisualNode(buildNode(root)));

    const visualRoot: VisualOrgNode = {
      id: '__org_root__',
      employee: null,
      children: rootChildren,
      groupedReports: [],
      childCount: rootChildren.length,
      directCount: rootChildren.length,
      totalDescendants: employees.length,
      isSynthetic: true,
    };

    if (rootChildren.length === 0) {
      return {
        width: 640,
        height: 280,
        cardWidth,
        cardHeight,
        nodes: [],
        links: [],
        rootIds: [],
      };
    }

    const rootHierarchy = hierarchy<VisualOrgNode>(visualRoot, (node) => node.children);
    const layoutRoot = tree<VisualOrgNode>()
      .nodeSize([horizontalStep, depthStep])
      .separation((left, right) => (left.parent === right.parent ? 0.95 : 1.18))(rootHierarchy);

    const descendants = layoutRoot.descendants().filter((node) => !node.data.isSynthetic);
    const minX = Math.min(...descendants.map((node) => node.x));
    const maxX = Math.max(...descendants.map((node) => node.x));
    const minY = Math.min(...descendants.map((node) => node.y));
    const maxY = Math.max(...descendants.map((node) => node.y));
    const xOffset = MAP_PADDING - minX;
    const yOffset = MAP_PADDING - minY;
    const positionedById = new Map<string, PositionedOrgNode>();

    const nodes = descendants.map((node) => {
      const positioned = {
        id: node.data.id,
        data: node.data,
        x: node.x + xOffset,
        y: node.y + yOffset,
      };
      positionedById.set(positioned.id, positioned);
      return positioned;
    });

    const links = layoutRoot.links()
      .filter((link) => !link.source.data.isSynthetic && !link.target.data.isSynthetic)
      .map((link) => {
        const source = positionedById.get(link.source.data.id)!;
        const target = positionedById.get(link.target.data.id)!;
        const sourceX = source.x + cardWidth / 2;
        const sourceY = source.y + cardHeight;
        const targetX = target.x + cardWidth / 2;
        const targetY = target.y;

        return {
          id: `${source.id}-${target.id}`,
          sourceId: source.id,
          targetId: target.id,
          path: buildElbowPath(sourceX, sourceY, targetX, targetY),
        };
      });

    return {
      width: Math.max(640, maxX - minX + cardWidth + MAP_PADDING * 2),
      height: Math.max(320, maxY - minY + cardHeight + MAP_PADDING * 2),
      cardWidth,
      cardHeight,
      nodes,
      links,
      rootIds: rootChildren.map((node) => node.id),
    };
  }, [
    effectiveDensityMode,
    descendantCountById,
    employees.length,
    expandedNodes,
    sortedRoots,
    visibleNodes,
    buildNode,
  ]);

  const positionedById = useMemo(
    () => new Map<string, PositionedOrgNode>(orgLayout.nodes.map((node) => [node.id, node])),
    [orgLayout]
  );

  // Matches in chart order (top of the tree first) — Enter in search steps through them.
  const orderedMatchIds = useMemo(
    () => (filterActive ? orgLayout.nodes.filter((node) => searchMatches.has(node.id)).map((node) => node.id) : []),
    [filterActive, orgLayout, searchMatches]
  );

  // ── Org stats ──
  const stats = useMemo(() => {
    const teams: Record<string, number> = {};
    const depts: Record<string, number> = {};
    let managersCount = 0;
    let hybridCount = 0;

    employees.forEach((emp) => {
      const team = getEmployeeTeamLabel(emp);
      teams[team] = (teams[team] || 0) + 1;
      const dept = emp.department?.name || 'Unassigned';
      depts[dept] = (depts[dept] || 0) + 1;
      if ((reportsByManager.get(emp._id) || []).length > 0) managersCount++;
      if (emp.wfhEligible) hybridCount++;
    });

    return {
      total: employees.length,
      managers: managersCount,
      hybrid: hybridCount,
      roots: roots.length,
      teams: Object.entries(teams).sort((a, b) => b[1] - a[1]),
      departments: Object.entries(depts).sort((a, b) => b[1] - a[1]),
    };
  }, [employees, reportsByManager, roots]);

  const salesDepartments = useMemo(
    () => departments.filter((department) => department.isSalesTeam),
    [departments]
  );

  const salesDepartmentNames = useMemo(
    () => new Set(salesDepartments.map((department) => department.name)),
    [salesDepartments]
  );

  const salesEmployees = useMemo(
    () => employees.filter((employee) => isSalesEmployee(employee, salesDepartmentNames)),
    [employees, salesDepartmentNames]
  );

  const salesManagerLanes = useMemo<SalesManagerLane[]>(() => {
    const managerIdsFromReports = new Set<string>();
    salesEmployees.forEach((employee) => {
      const managerEmployeeId = getEffectiveManagerEmployeeId(employee, employeeHierarchy);
      if (managerEmployeeId && employeeById.has(managerEmployeeId)) {
        managerIdsFromReports.add(managerEmployeeId);
      }
    });

    const departmentManagers = new Map<string, Department[]>();
    salesDepartments.forEach((department) => {
      const managerEmployeeId = department.managerUserId
        ? employeeHierarchy.employeeIdByUserId.get(department.managerUserId)
        : department.manager
          ? employeeHierarchy.employeeIdByName.get(department.manager.trim().toLowerCase())
          : null;

      if (!managerEmployeeId || !employeeById.has(managerEmployeeId)) return;
      managerIdsFromReports.add(managerEmployeeId);
      if (!departmentManagers.has(managerEmployeeId)) {
        departmentManagers.set(managerEmployeeId, []);
      }
      departmentManagers.get(managerEmployeeId)!.push(department);
    });

    return Array.from(managerIdsFromReports)
      .map((managerId) => {
        const manager = employeeById.get(managerId);
        if (!manager) return null;

        const directReports = salesEmployees
          .filter((employee) => getEffectiveManagerEmployeeId(employee, employeeHierarchy) === managerId)
          .sort(sortByName);
        const ownedDepartments = departmentManagers.get(managerId) || [];
        const ownedDepartment = ownedDepartments[0] || null;
        const managerSalesDepartment = manager.department?.isSalesTeam ? manager.department : null;
        const reportSalesDepartment = directReports[0]?.department?.isSalesTeam ? directReports[0].department : null;
        const departmentId =
          ownedDepartment?._id ||
          ownedDepartment?.id ||
          managerSalesDepartment?._id ||
          managerSalesDepartment?.id ||
          reportSalesDepartment?._id ||
          reportSalesDepartment?.id ||
          null;
        const departmentName =
          ownedDepartment?.name ||
          managerSalesDepartment?.name ||
          reportSalesDepartment?.name ||
          directReports[0]?.department?.name ||
          manager.department?.name ||
          'Sales';
        const teamName =
          manager.teamName ||
          ownedDepartment?.name ||
          managerSalesDepartment?.name ||
          reportSalesDepartment?.name ||
          directReports[0]?.teamName ||
          departmentName;
        const source: SalesManagerLane['source'] = ownedDepartments.length > 0 && directReports.length > 0
          ? 'both'
          : ownedDepartments.length > 0
            ? 'department'
            : 'reports';

        return {
          id: manager._id,
          manager,
          teamName,
          departmentId,
          departmentName,
          employees: directReports,
          visibleEmployees: directReports.filter((employee) => matchesFilters(employee)),
          directCount: directReports.length,
          linked: Boolean(manager.userId),
          source,
        };
      })
      .filter((lane): lane is SalesManagerLane => Boolean(lane))
      .sort((a, b) => {
        if (a.directCount !== b.directCount) return b.directCount - a.directCount;
        return a.manager.name.localeCompare(b.manager.name);
      });
  }, [employeeById, employeeHierarchy, salesDepartments, salesEmployees, matchesFilters]);

  const visibleLaneCount = useMemo(
    () => salesManagerLanes.reduce((sum, lane) => sum + lane.visibleEmployees.length, 0),
    [salesManagerLanes]
  );

  const listRows = useMemo(
    () => employees.filter((emp) => matchesFilters(emp)).sort(sortByName),
    [employees, matchesFilters]
  );

  // ── Selection ──
  const managerIdOf = useCallback(
    (id: string): string | null => {
      const employee = employeeById.get(id);
      if (!employee) return null;
      const mid = getEffectiveManagerEmployeeId(employee, employeeHierarchy);
      return mid && employeeById.has(mid) ? mid : null;
    },
    [employeeById, employeeHierarchy]
  );

  /** Everyone above `id`, nearest manager first. */
  const ancestorsOf = useCallback(
    (id: string) => {
      const chain: string[] = [];
      const seen = new Set<string>([id]);
      let current = managerIdOf(id);
      while (current && !seen.has(current)) {
        chain.push(current);
        seen.add(current);
        current = managerIdOf(current);
      }
      return chain;
    },
    [managerIdOf]
  );

  /** Select a person; with `reveal`, open their reporting line and bring them into view. */
  const selectPerson = useCallback(
    (id: string | null, options?: { reveal?: boolean }) => {
      setFocusedId(id);
      if (!id || !options?.reveal) return;
      const ancestors = ancestorsOf(id);
      setExpandedNodes((prev) => {
        if (ancestors.every((ancestor) => prev.has(ancestor))) return prev;
        const next = new Set(prev);
        ancestors.forEach((ancestor) => next.add(ancestor));
        return next;
      });
      setRevealId(id);
    },
    [ancestorsOf]
  );

  /** A card, lane row or list row picked a person. Keyboard picks move focus into the rail. */
  const handleNodeSelect = useCallback(
    (id: string, via: SelectVia = 'pointer') => {
      const active = document.activeElement;
      selectionOriginRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
      if (via === 'keyboard') setRailFocusTick((tick) => tick + 1);
      selectPerson(id);
    },
    [selectPerson]
  );
  /** A person picked inside the rail: the list they were in is replaced, so focus goes to the rail heading. */
  const handleRailSelect = useCallback(
    (id: string) => {
      setRailFocusTick((tick) => tick + 1);
      selectPerson(id, { reveal: true });
    },
    [selectPerson]
  );
  const handleRevealSelect = useCallback((id: string) => selectPerson(id, { reveal: true }), [selectPerson]);
  const clearSelection = useCallback(() => {
    if (focusedId) pendingReturnRef.current = focusedId;
    setFocusedId(null);
  }, [focusedId]);
  const handleViewProfile = useCallback((id: string) => navigate(`/employees/${id}`), [navigate]);

  // ── Handlers ──
  const handleToggle = useCallback((id: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedNodes(new Set(employees.map((e) => e._id)));
  }, [employees]);

  const collapseAll = useCallback(() => {
    const initial = new Set<string>();
    roots.forEach((r) => initial.add(r._id));
    setExpandedNodes(initial);
  }, [roots]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setDepartmentFilter(ALL_DEPARTMENTS);
  }, []);

  const openEditor = useCallback(
    (emp: Employee) => {
      if (!canEditOrg) return;
      setSaveError(null);
      setSelected(emp);
      setManagerId(getEffectiveManagerEmployeeId(emp, employeeHierarchy) || '');
      setManagerName(emp.managerName || '');
      setTeamName(emp.teamName || '');
    },
    [canEditOrg, employeeHierarchy]
  );

  const handleSave = async () => {
    if (!selected || !canEditOrg) return;
    setSaveError(null);
    const newManagerId = managerId || null;
    const manager = newManagerId ? employeeById.get(newManagerId) : null;
    const resolvedManagerName = newManagerId ? manager?.name || null : managerName.trim() || null;
    const resolvedManagerEmployeeId = resolveManagerEmployeeId(
      {
        managerEmployeeId: newManagerId,
        managerName: resolvedManagerName,
      },
      employeeHierarchy
    );

    if (resolvedManagerEmployeeId === selected._id) {
      setSnackbar({
        open: true,
        message: `${selected.name} can't report to themselves. Choose a different manager.`,
        severity: 'error',
      });
      return;
    }

    if (resolvedManagerEmployeeId && isEmployeeDescendant(
      selected._id,
      resolvedManagerEmployeeId,
      employeeHierarchy
    )) {
      setSnackbar({
        open: true,
        message: `${selected.name} can't report to someone on their own team. Choose a manager outside their reporting line.`,
        severity: 'error',
      });
      return;
    }

    try {
      await updateEmployee({
        id: selected._id,
        employee: {
          managerUserId: manager?.userId || null,
          managerName: resolvedManagerName,
          teamName: teamName || null,
        },
      }).unwrap();
      setSelected(null);
      setSnackbar({ open: true, message: `Saved ${selected.name}'s placement.`, severity: 'success' });
    } catch (error) {
      // Shown in the dialog: a toast behind an open modal is hidden from screen readers and out of reach.
      setSaveError(
        describeSaveError(error, `Couldn't save ${selected.name}'s placement.`, { manager: manager ?? null, keepsForm: true })
      );
    }
  };

  const isDescendant = useCallback(
    (ancestorId: string, targetId: string): boolean => {
      return isEmployeeDescendant(ancestorId, targetId, employeeHierarchy);
    },
    [employeeHierarchy]
  );

  const handleDragStart = useCallback((id: string) => {
    if (!canEditOrg) return;
    setDraggedId(id);
    setDragOverId(null);
    setTeamDragOverName(null);
  }, [canEditOrg]);

  // A drag that ends without a drop (Escape, or released off a target) leaves nothing dimmed.
  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
    setTeamDragOverName(null);
  }, []);

  const handleDrop = useCallback(
    async (target: Employee) => {
      if (!canEditOrg) {
        setDraggedId(null);
        setDragOverId(null);
        setTeamDragOverName(null);
        return;
      }

      if (!draggedId || draggedId === target._id) {
        setDraggedId(null);
        setDragOverId(null);
        setTeamDragOverName(null);
        return;
      }
      if (isDescendant(draggedId, target._id)) {
        const movedName = employeeById.get(draggedId)?.name || 'This person';
        setSnackbar({
          open: true,
          message: `${movedName} can't report to ${target.name}, who is on their own team.`,
          severity: 'error',
        });
        setDraggedId(null);
        setDragOverId(null);
        setTeamDragOverName(null);
        return;
      }

      const moved = employeeById.get(draggedId);
      if (!moved) {
        setDraggedId(null);
        setDragOverId(null);
        setTeamDragOverName(null);
        return;
      }

      try {
        await updateEmployee({
          id: moved._id,
          employee: { managerUserId: target.userId || null, managerName: target.name },
        }).unwrap();
        setSnackbar({
          open: true,
          message: `Moved ${moved.name} under ${target.name}.`,
          severity: 'success',
        });
      } catch (error) {
        const copy = describeSaveError(error, `Couldn't move ${moved.name} under ${target.name}.`, { manager: target });
        setSnackbar({ open: true, message: copy.message, action: copy.action, severity: 'error' });
      }

      setDraggedId(null);
      setDragOverId(null);
      setTeamDragOverName(null);
    },
    [canEditOrg, draggedId, employeeById, isDescendant, updateEmployee]
  );

  const handleDragEnter = useCallback(
    (id: string) => {
      if (canEditOrg && draggedId && id !== draggedId) setDragOverId(id);
    },
    [canEditOrg, draggedId]
  );

  const handleDragLeave = useCallback(() => setDragOverId(null), []);

  const handleTeamDragEnter = useCallback(
    (laneId: string) => {
      if (canEditOrg && draggedId) {
        setDragOverId(null);
        setTeamDragOverName(laneId);
      }
    },
    [canEditOrg, draggedId]
  );

  const handleTeamDragLeave = useCallback(() => setTeamDragOverName(null), []);

  const handleTeamDrop = useCallback(
    async (targetLane: SalesManagerLane) => {
      if (!canEditOrg) {
        setDraggedId(null);
        setTeamDragOverName(null);
        return;
      }

      if (!draggedId) {
        setTeamDragOverName(null);
        return;
      }

      const moved = employeeById.get(draggedId);
      if (!moved) {
        setDraggedId(null);
        setTeamDragOverName(null);
        return;
      }

      if (!isSalesEmployee(moved, salesDepartmentNames)) {
        setSnackbar({
          open: true,
          message: `${moved.name} isn't on a Sales team. Only Sales team members can move between these lanes.`,
          severity: 'error',
        });
        setDraggedId(null);
        setTeamDragOverName(null);
        return;
      }

      if (moved._id === targetLane.manager._id) {
        setSnackbar({ open: true, message: `${moved.name} can't report to themselves.`, severity: 'error' });
        setDraggedId(null);
        setTeamDragOverName(null);
        return;
      }

      if (isDescendant(moved._id, targetLane.manager._id)) {
        setSnackbar({
          open: true,
          message: `${moved.name} can't report to ${targetLane.manager.name}, who is on their own team.`,
          severity: 'error',
        });
        setDraggedId(null);
        setTeamDragOverName(null);
        return;
      }

      const currentManagerId = getEffectiveManagerEmployeeId(moved, employeeHierarchy);
      if (
        currentManagerId === targetLane.manager._id
        && getEmployeeTeamLabel(moved) === targetLane.teamName
        && (!targetLane.departmentId || moved.department?._id === targetLane.departmentId || moved.department?.id === targetLane.departmentId)
      ) {
        setDraggedId(null);
        setTeamDragOverName(null);
        return;
      }

      setTeamMoveSavingId(moved._id);
      try {
        await updateEmployee({
          id: moved._id,
          employee: {
            managerName: targetLane.manager.name,
            managerUserId: targetLane.manager.userId || null,
            teamName: targetLane.teamName,
            ...(targetLane.departmentId ? { department: targetLane.departmentId } : {}),
          },
        }).unwrap();
        setFocusedId(moved._id);
        setSnackbar({
          open: true,
          message: `Moved ${moved.name} to ${targetLane.manager.name}'s team.`,
          severity: 'success',
        });
      } catch (error) {
        const copy = describeSaveError(error, `Couldn't move ${moved.name} to ${targetLane.manager.name}'s team.`, {
          manager: targetLane.manager,
        });
        setSnackbar({ open: true, message: copy.message, action: copy.action, severity: 'error' });
      }

      setDraggedId(null);
      setDragOverId(null);
      setTeamDragOverName(null);
      setTeamMoveSavingId(null);
    },
    [canEditOrg, draggedId, employeeById, employeeHierarchy, isDescendant, salesDepartmentNames, updateEmployee]
  );

  // ── Pan & zoom ──
  // Every zoom change keeps the point under `anchor` (default: the viewport center) still.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const pending = pendingScrollRef.current;
    if (container && pending) {
      container.scrollLeft = pending.left;
      container.scrollTop = pending.top;
    }
    pendingScrollRef.current = null;
  }, [zoom]);

  const applyZoom = useCallback((value: number, anchor?: { x: number; y: number }) => {
    const next = clampZoom(value);
    const previous = zoomRef.current;
    if (Math.abs(next - previous) < 0.0005) return;
    const container = containerRef.current;
    zoomRef.current = next;
    if (container) {
      const anchorX = anchor?.x ?? container.clientWidth / 2;
      const anchorY = anchor?.y ?? container.clientHeight / 2;
      const contentX = (container.scrollLeft + anchorX) / previous;
      const contentY = (container.scrollTop + anchorY) / previous;
      pendingScrollRef.current = { left: contentX * next - anchorX, top: contentY * next - anchorY };
    }
    flushSync(() => setZoom(next));
  }, []);

  const cancelScrollAnimation = useCallback(() => {
    if (scrollAnimationRef.current !== null) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  }, []);

  const animateScrollTo = useCallback(
    (left: number, top: number) => {
      const container = containerRef.current;
      if (!container) return;
      cancelScrollAnimation();
      const toLeft = Math.max(0, Math.min(left, container.scrollWidth - container.clientWidth));
      const toTop = Math.max(0, Math.min(top, container.scrollHeight - container.clientHeight));
      if (prefersReducedMotion()) {
        container.scrollLeft = toLeft;
        container.scrollTop = toTop;
        return;
      }
      const fromLeft = container.scrollLeft;
      const fromTop = container.scrollTop;
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / SCROLL_ANIMATION_MS);
        const eased = easeEnter(t);
        container.scrollLeft = fromLeft + (toLeft - fromLeft) * eased;
        container.scrollTop = fromTop + (toTop - fromTop) * eased;
        scrollAnimationRef.current = t < 1 ? requestAnimationFrame(step) : null;
      };
      scrollAnimationRef.current = requestAnimationFrame(step);
    },
    [cancelScrollAnimation]
  );

  const animateZoomTo = useCallback(
    (target: number, anchor?: { x: number; y: number }, onDone?: () => void) => {
      if (zoomAnimationRef.current !== null) cancelAnimationFrame(zoomAnimationRef.current);
      cancelScrollAnimation();
      const from = zoomRef.current;
      const to = clampZoom(target);
      if (prefersReducedMotion() || Math.abs(to - from) < 0.001) {
        applyZoom(to, anchor);
        if (onDone) requestAnimationFrame(onDone);
        return;
      }
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / ZOOM_ANIMATION_MS);
        applyZoom(from + (to - from) * easeEnter(t), anchor);
        if (t < 1) {
          zoomAnimationRef.current = requestAnimationFrame(step);
        } else {
          zoomAnimationRef.current = null;
          if (onDone) requestAnimationFrame(onDone);
        }
      };
      zoomAnimationRef.current = requestAnimationFrame(step);
    },
    [applyZoom, cancelScrollAnimation]
  );

  useEffect(
    () => () => {
      if (zoomAnimationRef.current !== null) cancelAnimationFrame(zoomAnimationRef.current);
      if (scrollAnimationRef.current !== null) cancelAnimationFrame(scrollAnimationRef.current);
    },
    []
  );

  const centerOnNode = useCallback(
    (id: string) => {
      const container = containerRef.current;
      const node = positionedById.get(id);
      if (!container || !node) return;
      const scale = zoomRef.current;
      animateScrollTo(
        (node.x + orgLayout.cardWidth / 2) * scale - container.clientWidth / 2,
        (node.y + orgLayout.cardHeight / 2) * scale - container.clientHeight / 2
      );
    },
    [animateScrollTo, orgLayout.cardHeight, orgLayout.cardWidth, positionedById]
  );

  /** Scroll just enough to show a card with a margin (no move when it's already in view). */
  const revealNode = useCallback(
    (id: string) => {
      const container = containerRef.current;
      const node = positionedById.get(id);
      if (!container || !node) return;
      const scale = zoomRef.current;
      // The content is centered (mx: auto) while it is narrower than the canvas.
      const offsetX = Math.max(0, (container.clientWidth - orgLayout.width * scale) / 2);
      const left = offsetX + node.x * scale;
      const right = left + orgLayout.cardWidth * scale;
      const top = node.y * scale;
      const bottom = top + orgLayout.cardHeight * scale;
      const viewLeft = container.scrollLeft;
      const viewTop = container.scrollTop;
      let nextLeft = viewLeft;
      let nextTop = viewTop;
      if (left < viewLeft + REVEAL_MARGIN) nextLeft = left - REVEAL_MARGIN;
      else if (right > viewLeft + container.clientWidth - REVEAL_MARGIN) nextLeft = right - container.clientWidth + REVEAL_MARGIN;
      if (top < viewTop + REVEAL_MARGIN) nextTop = top - REVEAL_MARGIN;
      else if (bottom > viewTop + container.clientHeight - REVEAL_MARGIN) nextTop = bottom - container.clientHeight + REVEAL_MARGIN;
      if (nextLeft !== viewLeft || nextTop !== viewTop) animateScrollTo(nextLeft, nextTop);
    },
    [animateScrollTo, orgLayout.cardHeight, orgLayout.cardWidth, orgLayout.width, positionedById]
  );

  // When the canvas changes width (the detail rail opens or closes, the window or sidebar
  // resizes), keep the middle of the view still, then make sure the selected card is showing.
  const revealSelectedRef = useRef<() => void>(() => undefined);
  revealSelectedRef.current = () => {
    if (viewMode === 'tree' && focusedId && positionedById.has(focusedId)) revealNode(focusedId);
  };
  useEffect(() => {
    if (!canvasEl || typeof ResizeObserver === 'undefined') return undefined;
    let previousWidth = canvasEl.clientWidth;
    const observer = new ResizeObserver(() => {
      const nextWidth = canvasEl.clientWidth;
      const delta = previousWidth - nextWidth;
      previousWidth = nextWidth;
      if (!delta) return;
      canvasEl.scrollLeft += delta / 2;
      revealSelectedRef.current();
    });
    observer.observe(canvasEl);
    return () => observer.disconnect();
  }, [canvasEl]);

  // ── Keyboard: one tab stop on the canvas, arrow keys between cards (roving tabindex) ──
  const cardNav = useMemo(() => {
    const parentById = new Map<string, string>();
    const rows = new Map<number, PositionedOrgNode[]>();
    orgLayout.nodes.forEach((node) => {
      node.data.children.forEach((child) => parentById.set(child.id, node.id));
      const row = Math.round(node.y);
      if (!rows.has(row)) rows.set(row, []);
      rows.get(row)!.push(node);
    });
    const neighbors = new Map<string, { prev: string | null; next: string | null }>();
    rows.forEach((row) => {
      row.sort((a, b) => a.x - b.x);
      row.forEach((node, index) => {
        neighbors.set(node.id, { prev: row[index - 1]?.id ?? null, next: row[index + 1]?.id ?? null });
      });
    });
    return { parentById, neighbors };
  }, [orgLayout.nodes]);

  const rovingId =
    activeCardId && positionedById.has(activeCardId)
      ? activeCardId
      : focusedId && positionedById.has(focusedId)
        ? focusedId
        : ceo && positionedById.has(ceo._id)
          ? ceo._id
          : orgLayout.nodes[0]?.id ?? null;

  const focusCard = useCallback(
    (id: string) => {
      const card = containerRef.current?.querySelector<HTMLElement>(`[data-org-card="${cssEscape(id)}"]`);
      if (!card) return;
      card.focus({ preventScroll: true });
      revealNode(id);
    },
    [revealNode]
  );

  const cardKeyDownRef = useRef<(id: string, event: React.KeyboardEvent<HTMLElement>) => void>(() => undefined);
  cardKeyDownRef.current = (id, event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const node = positionedById.get(id);
    if (!node) return;
    let target: string | null | undefined = null;
    switch (event.key) {
      case 'ArrowUp':
        target = cardNav.parentById.get(id);
        break;
      case 'ArrowDown':
        target = node.data.children[0]?.id;
        // A collapsed team opens first; the next ArrowDown moves into it.
        if (!target && node.data.childCount > 0 && !expandedNodes.has(id)) {
          event.preventDefault();
          handleToggle(id);
          return;
        }
        break;
      case 'ArrowLeft':
        target = cardNav.neighbors.get(id)?.prev;
        break;
      case 'ArrowRight':
        target = cardNav.neighbors.get(id)?.next;
        break;
      case 'Home':
        target = orgLayout.rootIds[0];
        break;
      case 'e':
      case 'E': {
        const employee = employeeById.get(id);
        if (!canEditOrg || !employee) return;
        event.preventDefault();
        openEditor(employee);
        return;
      }
      default:
        return;
    }
    event.preventDefault();
    if (target) focusCard(target);
  };
  const handleCardKeyDown = useCallback(
    (id: string, event: React.KeyboardEvent<HTMLElement>) => cardKeyDownRef.current(id, event),
    []
  );

  // ── Zoom controls ──
  const zoomIn = () => animateZoomTo(zoomRef.current + ZOOM_STEP);
  const zoomOut = () => animateZoomTo(zoomRef.current - ZOOM_STEP);
  const resetZoom = () => animateZoomTo(1);
  const fitToScreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      applyZoom(employees.length > 80 ? 0.55 : employees.length > 40 ? 0.7 : 0.85);
      return;
    }

    const nextZoom = Math.min(
      1,
      Math.max(
        0.35,
        Math.min(
          (container.clientWidth - 32) / orgLayout.width,
          (container.clientHeight - 32) / orgLayout.height
        )
      )
    );
    const anchor = ceo ? positionedById.get(ceo._id) : undefined;
    // Re-center on CEO after zoom change
    animateZoomTo(nextZoom, undefined, () => {
      const element = containerRef.current;
      if (!element) return;
      const left = anchor ? (anchor.x + orgLayout.cardWidth / 2) * nextZoom - element.clientWidth / 2 : 0;
      animateScrollTo(left, 0);
    });
  }, [animateScrollTo, animateZoomTo, applyZoom, ceo, employees.length, orgLayout, positionedById]);

  // Ctrl/⌘ + wheel (and trackpad pinch) zooms around the pointer; a plain wheel scrolls.
  useEffect(() => {
    if (!canvasEl) return undefined;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) {
        cancelScrollAnimation();
        return;
      }
      event.preventDefault();
      if (zoomAnimationRef.current !== null) {
        cancelAnimationFrame(zoomAnimationRef.current);
        zoomAnimationRef.current = null;
      }
      cancelScrollAnimation();
      const rect = canvasEl.getBoundingClientRect();
      applyZoom(zoomRef.current * Math.exp(-event.deltaY * 0.0025), {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    };
    canvasEl.addEventListener('wheel', onWheel, { passive: false });
    return () => canvasEl.removeEventListener('wheel', onWheel);
  }, [applyZoom, canvasEl, cancelScrollAnimation]);

  // Drag the empty canvas to pan; a click on it (no drag) clears the selection.
  const handleCanvasPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.pointerType === 'touch') return;
    const container = event.currentTarget;
    const target = event.target as HTMLElement;
    if (target.closest('[data-org-node]')) return;
    const rect = container.getBoundingClientRect();
    // Leave the native scrollbars alone.
    if (event.clientX - rect.left > container.clientWidth || event.clientY - rect.top > container.clientHeight) return;
    cancelScrollAnimation();
    panRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      left: container.scrollLeft,
      top: container.scrollTop,
      moved: false,
    };
    container.setPointerCapture(event.pointerId);
    container.dataset.panning = 'true';
  };

  const handleCanvasPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    const dx = event.clientX - pan.x;
    const dy = event.clientY - pan.y;
    if (!pan.moved && Math.abs(dx) + Math.abs(dy) > 4) pan.moved = true;
    event.currentTarget.scrollLeft = pan.left - dx;
    event.currentTarget.scrollTop = pan.top - dy;
  };

  const endPan = (event: React.PointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    if (!pan || pan.pointerId !== event.pointerId) return;
    panRef.current = null;
    const container = event.currentTarget;
    if (container.hasPointerCapture(event.pointerId)) container.releasePointerCapture(event.pointerId);
    delete container.dataset.panning;
    if (!pan.moved && event.type === 'pointerup') clearSelection();
  };

  const handleCanvasKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.defaultPrevented) return;
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      zoomIn();
    } else if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      zoomOut();
    } else if (event.key === '0') {
      event.preventDefault();
      fitToScreen();
    } else if (event.key === 'Escape' && focusedId) {
      event.preventDefault();
      clearSelection();
    }
  };

  // Center the CEO card once the tree first renders (and again when returning to the tree).
  // Runs before paint, once the default expansion is in the layout, so the chart never jumps.
  useLayoutEffect(() => {
    if (hasScrolledToAnchor || !canvasEl || viewMode !== 'tree' || expandedNodes.size === 0 || !ceo) return;
    const anchor = positionedById.get(ceo._id);
    if (!anchor) return;
    const scale = zoomRef.current;
    canvasEl.scrollLeft = Math.max(0, (anchor.x + orgLayout.cardWidth / 2) * scale - canvasEl.clientWidth / 2);
    canvasEl.scrollTop = 0;
    setHasScrolledToAnchor(true);
  }, [hasScrolledToAnchor, canvasEl, viewMode, expandedNodes.size, ceo, positionedById, orgLayout.cardWidth]);

  // Bring a person picked from search, the detail panel or a list into view.
  useEffect(() => {
    if (!revealId) return;
    if (viewMode === 'tree') {
      const target = [revealId, ...ancestorsOf(revealId)].find((id) => positionedById.has(id));
      if (target) centerOnNode(target);
    }
    setRevealId(null);
  }, [revealId, viewMode, ancestorsOf, positionedById, centerOnNode]);

  // When the search or department filter settles, center the first match.
  const filterKey = filterActive ? `${searchTerm.trim().toLowerCase()}|${departmentFilter}` : '';
  useEffect(() => {
    setMatchCursor(-1);
  }, [filterKey]);

  useEffect(() => {
    if (viewMode !== 'tree' || filterKey === lastCenteredFilterRef.current) return undefined;
    const timer = window.setTimeout(() => {
      lastCenteredFilterRef.current = filterKey;
      if (filterKey && orderedMatchIds[0]) centerOnNode(orderedMatchIds[0]);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [filterKey, orderedMatchIds, viewMode, centerOnNode]);

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || orderedMatchIds.length === 0) return;
    event.preventDefault();
    const next = (matchCursor + 1) % orderedMatchIds.length;
    setMatchCursor(next);
    selectPerson(orderedMatchIds[next], { reveal: true });
  };

  const handlePrint = () => {
    // The button is disabled (with a reason) while the chart is empty.
    if (orgLayout.nodes.length === 0) return;

    const printWindow = window.open('', '_blank', 'width=1280,height=900');
    if (!printWindow) {
      setSnackbar({
        open: true,
        message: "Couldn't open the print window. Allow pop-ups for this site, then try again.",
        severity: 'error',
      });
      return;
    }

    printWindow.document.open();
    printWindow.document.write(buildOrgChartPrintHtml({
      orgLayout,
      densityMode: effectiveDensityMode,
      searchTerm,
      filterLabel: activeDepartment?.label ?? null,
      stats,
      departments: departmentSwatches,
      generatedAt: new Date(),
    }));
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // ── Unique teams for the editor ──
  const uniqueTeams = useMemo(() => {
    const teams = new Set<string>();
    employees.forEach((e) => {
      const team = getEmployeeTeamLabel(e);
      if (team !== UNASSIGNED_TEAM) teams.add(team);
    });
    return Array.from(teams).sort();
  }, [employees]);

  const managerOptions = useMemo(
    () => (selected ? employees.filter((emp) => emp._id !== selected._id).sort(sortByName) : []),
    [employees, selected]
  );

  const managerNameOf = useCallback(
    (employee: Employee) => {
      const effectiveManagerId = getEffectiveManagerEmployeeId(employee, employeeHierarchy);
      const managerEmployee = effectiveManagerId ? employeeById.get(effectiveManagerId) : null;
      return managerEmployee?.name || employee.managerName || null;
    },
    [employeeById, employeeHierarchy]
  );

  // ── The selected person's record ──
  const selectedPerson = focusedId ? employeeById.get(focusedId) ?? null : null;
  const selectedDetail = useMemo<PersonDetailData | null>(() => {
    if (!selectedPerson) return null;
    const effectiveManagerId = getEffectiveManagerEmployeeId(selectedPerson, employeeHierarchy);
    const manager = effectiveManagerId ? employeeById.get(effectiveManagerId) ?? null : null;
    const reportingLine = ancestorsOf(selectedPerson._id)
      .map((id) => employeeById.get(id))
      .filter((person): person is Employee => Boolean(person))
      .reverse();
    return {
      employee: selectedPerson,
      manager,
      managerFallbackName: manager ? null : selectedPerson.managerName || null,
      reportingLine,
      directReports: reportsByManager.get(selectedPerson._id) || [],
      totalInOrg: descendantCountById.get(selectedPerson._id) || 0,
      department: departmentOf(selectedPerson),
    };
  }, [selectedPerson, employeeHierarchy, employeeById, ancestorsOf, reportsByManager, descendantCountById, departmentOf]);

  const showInlinePanel = isWide && Boolean(selectedDetail);

  // A keyboard pick (or a pick inside the rail) moves focus to the rail's heading.
  useEffect(() => {
    if (railFocusTick === railFocusHandledRef.current) return;
    railFocusHandledRef.current = railFocusTick;
    if (showInlinePanel) railHeadingRef.current?.focus();
  }, [railFocusTick, showInlinePanel]);

  // Closing the rail unmounts the button that had focus: hand focus back to the person's
  // card or row (the control that opened it when it's still there), never to the page body.
  useEffect(() => {
    if (focusedId) return;
    const closedId = pendingReturnRef.current;
    pendingReturnRef.current = null;
    if (!closedId) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const origin = selectionOriginRef.current;
    const originIsFor = (element: HTMLElement | null) =>
      Boolean(element?.isConnected)
      && (element!.getAttribute('data-org-card') === closedId || element!.getAttribute('data-select-person') === closedId);
    const panel = document.getElementById(VIEW_PANEL_ID);
    const id = cssEscape(closedId);
    const target = originIsFor(origin)
      ? origin
      : panel?.querySelector<HTMLElement>(`[data-org-card="${id}"]`)
        ?? panel?.querySelector<HTMLElement>(`[data-select-person="${id}"]`)
        ?? (origin?.isConnected ? origin : null)
        ?? containerRef.current;
    target?.focus();
  }, [focusedId]);

  // ── Chart layers (memoized apart from zoom so pan/zoom frames stay cheap) ──
  const searchHighlight = searchActive ? searchTerm : '';
  const overview = zoom < OVERVIEW_ZOOM;
  const nodeElements = useMemo(
    () =>
      orgLayout.nodes.map((item) => {
        const employee = item.data.employee;
        if (!employee) return null;
        const department = departmentOf(employee);
        const activeGroupedId =
          focusedId && item.data.groupedReports.some((report) => report._id === focusedId) ? focusedId : null;
        return (
          <Box
            key={item.id}
            data-org-node=""
            data-ceo={item.id === ceo?._id ? 'true' : undefined}
            style={{ left: item.x, top: item.y, width: orgLayout.cardWidth, height: orgLayout.cardHeight }}
            sx={{ position: 'absolute', animation: `${nodeEnter} ${sbg.duration.base} ${sbg.ease.enter} both` }}
          >
            <OrgNodeCard
              item={item}
              densityMode={effectiveDensityMode}
              isExpanded={expandedNodes.has(item.id)}
              isSelected={focusedId === item.id}
              isMatch={searchActive && searchMatches.has(item.id)}
              isContext={filterActive && !searchMatches.has(item.id)}
              isDragOver={dragOverId === item.id}
              isDragging={draggedId === item.id}
              activeGroupedId={activeGroupedId}
              searchTerm={searchHighlight}
              deptColor={department.color}
              deptLabel={department.label}
              canEdit={canEditOrg}
              isTabStop={item.id === rovingId}
              overview={overview}
              onSelect={handleNodeSelect}
              onCardKeyDown={handleCardKeyDown}
              onCardFocus={setActiveCardId}
              onToggle={handleToggle}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
            />
          </Box>
        );
      }),
    [
      orgLayout,
      departmentOf,
      focusedId,
      ceo,
      effectiveDensityMode,
      expandedNodes,
      searchActive,
      searchMatches,
      filterActive,
      dragOverId,
      draggedId,
      searchHighlight,
      canEditOrg,
      rovingId,
      overview,
      handleNodeSelect,
      handleCardKeyDown,
      handleToggle,
      handleDragStart,
      handleDragEnd,
      handleDrop,
      handleDragEnter,
      handleDragLeave,
    ]
  );

  const linkStroke = 1 / zoom;
  const sortedLinks = useMemo(() => {
    // Blue traces the selected person's links and the reporting lines down to search matches.
    const emphasized = (sourceId: string, targetId: string) =>
      focusedId === sourceId
      || focusedId === targetId
      || (searchActive && (searchMatches.has(targetId) || pathMatches.has(targetId)));
    return orgLayout.links
      .map((link) => ({ link, emphasized: emphasized(link.sourceId, link.targetId) }))
      .sort((a, b) => Number(a.emphasized) - Number(b.emphasized));
  }, [orgLayout.links, focusedId, searchActive, searchMatches, pathMatches]);

  const freshness = fulfilledTimeStamp ? `Updated ${formatTime(fulfilledTimeStamp)}` : null;
  const pageHeaderMeta = [
    formatCount(stats.total, 'active person', 'active people'),
    formatCount(realDepartmentCount, 'department'),
    formatCount(stats.managers, 'manager'),
    freshness,
  ];

  if (isLoading || departmentsLoading) {
    if (!loadStalled) return <PageLoading label="Loading the org chart…" variant="canvas" />;
    // The request never answered: say so, and offer the one retry that works.
    return (
      <Box>
        <PageHeader overline="Workspace" title="Org chart" />
        <Box sx={{ bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.card }}>
          <ErrorState
            title="The org chart is taking too long to load"
            body="The employee directory hasn’t answered in 15 seconds. Reload the page to ask again. If it keeps happening, tell an admin."
            retryLabel="Reload page"
            onRetry={reloadPage}
          />
        </Box>
      </Box>
    );
  }
  if (employeesError || departmentsError) {
    return (
      <Box>
        <PageHeader overline="Workspace" title="Org chart" />
        <PageError
          title="Couldn't load the org chart"
          description="The employee directory didn't respond. Try again in a minute."
          onRetry={() => { reloadEmployees(); reloadDepartments(); }}
        />
      </Box>
    );
  }

  const header = (
    <PageHeader
      overline="Workspace"
      title="Org chart"
      meta={pageHeaderMeta}
      actions={
        employees.length > 0 ? (
          <>
            <Button variant="text" startIcon={<ChartColumn size={16} />} onClick={() => setStatsOpen(true)}>
              Org summary
            </Button>
            <Tooltip title={orgLayout.nodes.length === 0 ? 'Nothing to print. Clear the filters first.' : ''}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button
                  variant="outlined"
                  startIcon={<Printer size={16} />}
                  onClick={handlePrint}
                  disabled={orgLayout.nodes.length === 0}
                >
                  Print chart
                </Button>
              </Box>
            </Tooltip>
          </>
        ) : undefined
      }
    >
      {employees.length > 0 && (
        <Tabs
          value={viewMode}
          onChange={(_, value: OrgViewMode) => {
            setViewMode(value);
            if (value === 'tree') setHasScrolledToAnchor(false);
          }}
          aria-label="Org chart views"
          variant="scrollable"
          scrollButtons={false}
        >
          <Tab
            value="tree"
            id={VIEW_TAB_ID('tree')}
            aria-controls={VIEW_PANEL_ID}
            icon={<Network size={16} />}
            iconPosition="start"
            label="Chart"
          />
          <Tab
            value="teams"
            id={VIEW_TAB_ID('teams')}
            aria-controls={VIEW_PANEL_ID}
            icon={<Users size={16} />}
            iconPosition="start"
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                Sales teams
                <CountBadge count={salesManagerLanes.length} aria-label={formatCount(salesManagerLanes.length, 'team')} />
              </Box>
            }
          />
          <Tab
            value="list"
            id={VIEW_TAB_ID('list')}
            aria-controls={VIEW_PANEL_ID}
            icon={<ListIcon size={16} />}
            iconPosition="start"
            label="List"
          />
        </Tabs>
      )}
    </PageHeader>
  );

  // First use: nobody active yet.
  if (employees.length === 0) {
    return (
      <Box>
        {header}
        <Box sx={{ bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.card }}>
          <EmptyState
            title="No one in the org chart yet"
            body="People appear here once they're added as active employees, with their managers on file."
            action={
              canEditOrg ? (
                <Button variant="outlined" onClick={() => navigate('/employees')}>
                  Go to employees
                </Button>
              ) : undefined
            }
          />
        </Box>
      </Box>
    );
  }

  const filteredEmpty = (
    <EmptyState
      title="No people match these filters"
      body={
        searchActive
          ? `Nothing matches “${searchTerm.trim()}”${activeDepartment ? ` in ${activeDepartment.label}` : ''}. Check the spelling or clear the filters.`
          : `No one in ${activeDepartment?.label ?? 'this department'} shows up here.`
      }
      action={
        <Button variant="outlined" onClick={clearFilters}>
          Clear filters
        </Button>
      }
    />
  );

  // ── Toolbar ──
  const toolbarEnd = (() => {
    if (viewMode === 'tree') {
      if (!filterActive) return null;
      const count = orderedMatchIds.length;
      const label = searchActive && matchCursor >= 0 && count > 0
        ? `${formatNumber(matchCursor + 1)} of ${formatNumber(count)}`
        : searchActive
          ? formatCount(count, 'match', 'matches')
          : formatCount(count, 'person', 'people');
      return (
        <Typography variant="body2" component="span" aria-live="polite" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
          {label}
          {searchActive && count > 1 ? ' · Enter for next' : ''}
        </Typography>
      );
    }
    if (viewMode === 'teams') {
      return (
        <>
          {teamMoveSavingId && <StatusPill tone="info">Saving move…</StatusPill>}
          <Typography variant="body2" component="span" aria-live="polite" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            {filterActive
              ? formatCount(visibleLaneCount, 'match', 'matches')
              : `${formatCount(salesEmployees.length, 'Sales person', 'Sales people')} · ${formatCount(salesManagerLanes.length, 'team')}`}
          </Typography>
        </>
      );
    }
    return (
      <Typography variant="body2" component="span" aria-live="polite" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
        {filterActive
          ? `Showing ${formatNumber(listRows.length)} of ${formatCount(employees.length, 'person', 'people')}`
          : formatCount(employees.length, 'person', 'people')}
      </Typography>
    );
  })();

  const toolbar = (
    <Toolbar end={toolbarEnd}>
      <SearchField
        value={searchTerm}
        onChange={setSearchTerm}
        label="Find a person"
        placeholder="Search name, email or team"
        width={300}
        onKeyDown={viewMode === 'tree' ? handleSearchKeyDown : undefined}
      />
      <Select
        size="small"
        value={departmentFilter}
        onChange={(event) => setDepartmentFilter(event.target.value as string)}
        inputProps={{ 'aria-label': 'Filter by department' }}
        renderValue={(value) => {
          if (value === ALL_DEPARTMENTS) return 'All departments';
          const swatch = swatchByKey.get(value as string);
          return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <DeptDot color={swatch?.color ?? NEUTRAL_SWATCH} />
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{swatch?.label ?? 'Department'}</Box>
            </Box>
          );
        }}
        sx={{ width: { xs: '100%', sm: 220 } }}
      >
        <MenuItem value={ALL_DEPARTMENTS}>All departments</MenuItem>
        {departmentOptions.map((swatch) => (
          <MenuItem key={swatch.key} value={swatch.key} sx={{ gap: 1 }}>
            <DeptDot color={swatch.color} />
            <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {swatch.label}
            </Box>
            <Box component="span" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(swatch.count)}
            </Box>
          </MenuItem>
        ))}
      </Select>
      {viewMode === 'tree' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title={filterActive ? 'Filtered charts show everyone who matches. Clear the filters to switch views.' : ''}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={effectiveDensityMode}
                disabled={filterActive}
                aria-label="People shown as cards"
                onChange={(_, value: DensityMode | null) => {
                  if (!value) return;
                  setDensityMode(value);
                  if (value === 'all') {
                    setExpandedNodes(new Set(employees.map((employee) => employee._id)));
                  }
                }}
              >
                <ToggleButton value="managers">Managers</ToggleButton>
                <ToggleButton value="all">All people</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <Tooltip title="Expand all">
            <IconButton size="small" aria-label="Expand all" onClick={expandAll}>
              <ChevronsUpDown />
            </IconButton>
          </Tooltip>
          <Tooltip title="Collapse to top level">
            <IconButton size="small" aria-label="Collapse to top level" onClick={collapseAll}>
              <ChevronsDownUp />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Toolbar>
  );

  // ── View bodies ──
  const treeBody = orgLayout.nodes.length === 0 ? (
    <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center', bgcolor: sbg.bg.surfaceSubtle }}>
      {filterActive ? filteredEmpty : (
        <EmptyState title="No reporting lines yet" body="Add managers to employee records to build the chart." />
      )}
    </Box>
  ) : (
    <Box sx={{ position: 'relative' }}>
      <Box
        ref={setCanvasRef}
        role="region"
        aria-label="Org chart canvas"
        aria-describedby="org-canvas-hint"
        tabIndex={0}
        onKeyDown={handleCanvasKeyDown}
        onPointerDown={handleCanvasPointerDown}
        onPointerMove={handleCanvasPointerMove}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        sx={{
          height: VIEW_HEIGHT,
          overflow: 'auto',
          bgcolor: sbg.bg.surfaceSubtle,
          cursor: 'grab',
          userSelect: 'none',
          overscrollBehavior: 'contain',
          '&[data-panning="true"]': { cursor: 'grabbing' },
          '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 },
        }}
      >
        <Box style={{ width: orgLayout.width * zoom, height: orgLayout.height * zoom }} sx={{ position: 'relative', mx: 'auto' }}>
          <Box
            style={{ width: orgLayout.width, height: orgLayout.height, transform: `scale(${zoom})` }}
            sx={{ position: 'absolute', left: 0, top: 0, transformOrigin: 'top left' }}
          >
            <svg
              width={orgLayout.width}
              height={orgLayout.height}
              aria-hidden="true"
              style={{ position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' }}
            >
              {sortedLinks.map(({ link, emphasized }) => (
                <path
                  key={link.id}
                  d={link.path}
                  fill="none"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  style={{
                    stroke: emphasized ? sbg.border.selected : sbg.border.strong,
                    strokeWidth: emphasized ? linkStroke * 1.5 : linkStroke,
                  }}
                />
              ))}
            </svg>
            {nodeElements}
          </Box>
        </Box>
      </Box>

      <ZoomControls zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} onReset={resetZoom} onFit={fitToScreen} />
    </Box>
  );

  const treeFooter = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${sbg.border.subtle}`,
      }}
    >
      <DepartmentLegend
        swatches={departmentSwatches}
        activeKey={departmentActive ? departmentFilter : null}
        onToggle={(key) => setDepartmentFilter((current) => (current === key ? ALL_DEPARTMENTS : key))}
      />
      <CanvasHint id="org-canvas-hint" canEdit={canEditOrg} />
    </Box>
  );

  const teamsBody = salesManagerLanes.length === 0 ? (
    <EmptyState
      title="No Sales teams yet"
      body="Sales managers and their people appear here once a department is marked as a Sales team."
      action={
        canEditOrg ? (
          <Button variant="outlined" onClick={() => navigate('/departments')}>
            Go to departments
          </Button>
        ) : undefined
      }
    />
  ) : filterActive && visibleLaneCount === 0 ? (
    filteredEmpty
  ) : (
    <Box
      sx={{
        p: 2,
        bgcolor: sbg.bg.surfaceSubtle,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
        gap: 2,
      }}
    >
      {salesManagerLanes.map((lane) => (
        <TeamLane
          key={lane.id}
          lane={lane}
          searchTerm={searchHighlight}
          filterActive={filterActive}
          reportsByManager={reportsByManager}
          dragOverLane={teamDragOverName}
          draggedId={draggedId}
          savingId={teamMoveSavingId}
          focusedId={focusedId}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragEnter={handleTeamDragEnter}
          onDragLeave={handleTeamDragLeave}
          onDrop={handleTeamDrop}
          onEdit={openEditor}
          onViewProfile={handleViewProfile}
          onSelect={handleNodeSelect}
          canEdit={canEditOrg}
          canViewProfile={canViewProfiles}
        />
      ))}
    </Box>
  );

  const teamsFooter = canEditOrg && salesManagerLanes.length > 0 ? (
    <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${sbg.border.subtle}` }}>
      <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <GripVertical size={14} aria-hidden="true" />
        Drag a Sales team member into another lane to change their manager and team.
      </Typography>
    </Box>
  ) : null;

  const listBody = (
    <Box role="region" aria-label="People list" tabIndex={0} sx={{ overflow: 'auto', maxHeight: { md: VIEW_HEIGHT.md }, '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 } }}>
      <OrgListTable
        rows={listRows}
        searchTerm={searchHighlight}
        focusedId={focusedId}
        reportsByManager={reportsByManager}
        managerNameOf={managerNameOf}
        departmentOf={departmentOf}
        filterActive={filterActive}
        onClearFilters={clearFilters}
        canEdit={canEditOrg}
        canViewProfile={canViewProfiles}
        onSelect={handleNodeSelect}
        onEdit={openEditor}
        onViewProfile={handleViewProfile}
      />
    </Box>
  );

  return (
    <Box ref={pageRef} sx={{ minWidth: 0 }}>
      {header}

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          alignItems: 'start',
          gridTemplateColumns: showInlinePanel ? 'minmax(0, 1fr) 340px' : 'minmax(0, 1fr)',
        }}
      >
        <Box
          component="section"
          role="tabpanel"
          id={VIEW_PANEL_ID}
          aria-labelledby={VIEW_TAB_ID(viewMode)}
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: sbg.bg.surface,
            border: `1px solid ${sbg.border.default}`,
            borderRadius: sbg.radius.card,
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${sbg.border.subtle}` }}>{toolbar}</Box>
          {viewMode === 'tree' ? (
            <>
              {treeBody}
              {orgLayout.nodes.length > 0 && treeFooter}
            </>
          ) : viewMode === 'teams' ? (
            <>
              {teamsBody}
              {teamsFooter}
            </>
          ) : (
            listBody
          )}
        </Box>

        {showInlinePanel && selectedDetail && (
          <PersonDetailPanel
            data={selectedDetail}
            canEdit={canEditOrg}
            canViewProfile={canViewProfiles}
            headingRef={railHeadingRef}
            onClose={clearSelection}
            onSelect={handleRailSelect}
            onEdit={openEditor}
            onViewProfile={handleViewProfile}
          />
        )}
      </Box>

      {/* Below `xl` the selected person opens in a sheet instead of the side rail. */}
      {!isWide && (
        <Sheet
          open={Boolean(selectedDetail)}
          onClose={clearSelection}
          title={selectedDetail?.employee.name ?? ''}
          subtitle={selectedDetail ? titleLineOf(selectedDetail.employee) : undefined}
          actions={
            selectedDetail && (canEditOrg || canViewProfiles) ? (
              <PersonDetailActions
                employee={selectedDetail.employee}
                canEdit={canEditOrg}
                canViewProfile={canViewProfiles}
                onEdit={openEditor}
                onViewProfile={handleViewProfile}
              />
            ) : undefined
          }
        >
          {selectedDetail ? <PersonDetailBody data={selectedDetail} onSelect={handleRevealSelect} showIdentity /> : null}
        </Sheet>
      )}

      {/* ── Edit placement ── */}
      <PlacementDialog
        employee={selected}
        managerOptions={managerOptions}
        managerId={managerId}
        managerName={managerName}
        teamName={teamName}
        teamOptions={uniqueTeams}
        directReportCount={selected ? (reportsByManager.get(selected._id) || []).length : 0}
        employeeById={employeeById}
        isSaving={isSaving}
        error={saveError}
        onErrorAction={(to) => navigate(to)}
        onManagerChange={(nextManagerId, nextManagerName) => {
          setManagerId(nextManagerId);
          setManagerName(nextManagerName);
        }}
        onManagerNameChange={setManagerName}
        onTeamChange={setTeamName}
        onClose={() => {
          setSelected(null);
          setSaveError(null);
        }}
        onSave={handleSave}
      />

      {/* ── Org summary ── */}
      <OrgSummarySheet
        open={statsOpen}
        onClose={() => setStatsOpen(false)}
        stats={stats}
        freshness={freshness}
        showHybrid={!directoryOnly}
        onPickTeam={(name) => {
          setSearchTerm(name);
          setStatsOpen(false);
        }}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 5000}
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSnackbar((s) => ({ ...s, open: false }));
        }}
      >
        <Alert
          severity={snackbar.severity}
          role={snackbar.severity === 'error' ? 'alert' : 'status'}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          action={
            snackbar.action ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    const to = snackbar.action?.to;
                    setSnackbar((s) => ({ ...s, open: false }));
                    if (to) navigate(to);
                  }}
                >
                  {snackbar.action.label}
                </Button>
                <Tooltip title="Dismiss">
                  <IconButton size="small" aria-label="Dismiss" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
                    <X size={16} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : undefined
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrgChart;
